import {
    LegacyCard,
    DescriptionList,
    Layout,
    Frame,
    Page,
    Button,
    LegacyStack,
    Spinner,
    Text,
} from "@shopify/polaris";
import {useCallback, useEffect} from 'react';
import { useState } from 'react';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import Api from '../../helpers/api';
import CustomerOnHold from "./CustomerOnHold";
import ApproveSection from "./ApproveSection";
import { AddressBlock } from './components/AddressBlock';
import { OnHoldToggle } from './components/OnHoldToggle';
import { LocationsCard } from './components/LocationsCard';
import { MergeModal } from './components/MergeModal';
import {getBusinessTypeName, getFileName} from "./index";
import {useToast} from "@shopify/app-bridge-react";
import {getValue} from "../Helpers";

const ApprovedOnly = ({ customer, children }) => (
    (customer.status === 'approved')
        ? <>{children}</>
        : null
)

const ChangeStatus = ({ customer, setCustomer }) => {
    if (customer.status === 'approved') {
        return null;
    }

    return (<>
        <ApproveSection customer={customer}
                        setCustomer={setCustomer}
        />
        <LegacyCard sectioned>
            {customer.default_location?.shipping && <AddressBlock
                label={'Shipping Address'}
                location={customer.default_location.shipping}
            />}
            <br />
            <hr />
            <br />
            {customer.default_location?.billing && <AddressBlock
                label={'Billing Address'}
                location={customer.default_location.billing}
            />}
        </LegacyCard>
    </>)
}

const CustomerFiles = ({ customer }) => {
    const toast = useToast();
    const [downloading, setDownloading] = useState(false);

    const downloadCustomerFile = useCallback((type) => {
        if (!customer[type] || customer[type].length == 0) {
            toast.show("Customer does not have this file.", {
                duration: 5000,
            });
            return;
        }

        setDownloading(true);
        Api
            .getCustomerFile(customer.id, type)
            .then(data => downloadFile(data, type))
            .catch(error => {
                toast.show("Server error", {duration: 5000})
            })
            .finally(() => setDownloading(false));

    }, [customer]);

    const downloadFile = function (response, type){
        const contentDisposition = response.headers['content-disposition'];
        const filenameMatch = contentDisposition?.match(/filename=(?<filename>[^,;]+)/);
        const filename = filenameMatch.length ? filenameMatch[1] : type;

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${filename}`);
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
    };

    const getFileDesc = (type) => {
        const desc = !customer[type] || customer[type].length == 0
        ? 'N/A'
        : <Button
                loading={downloading}
                size={"slim"}
                external
                url={`/customers/${customer.id}/file/${customer.file_keys[type]}/${type}`}
            >Download</Button>;

        return {
            term: getFileName(type),
            description: desc
        };
    }

    return (<>
        <LegacyCard sectioned title={"Customer Files"}>
            <DescriptionList
                items={[
                    getFileDesc('state_sales_tax_resale_file'),
                    getFileDesc('business_license_file'),
                    getFileDesc('proof_1_file'),
                    getFileDesc('proof_2_file'),
                    getFileDesc('proof_3_file'),
                    getFileDesc('proof_4_file'),
                ]}
            />
        </LegacyCard>
    </>)
}

const ShowCustomer = () => {
    const history = useHistory();
    const [customer, setCustomer] = useState(null);
    const {customerId} = useParams();
    const {search} = useLocation();
    const [backUrl] = useState(
        (new URLSearchParams(search)).get('back') || null
    );
    const [showMergeModal, setShowMergeModal] = useState(false);
    const closeMergeModal = useCallback(() => setShowMergeModal(false), [showMergeModal]);

    const placeOrder = (location_id) => {
        history.push(`/orders/${customer.id}?location_id=${location_id}`);
    }

    useEffect(() => {
        getCustomer(customerId);
        setShowMergeModal(false);
    }, [customerId]);

    const getCustomer = (customerId) => {
        Api.getCustomer(customerId).then( response => {
            setCustomer(response.data.data);
        });
    }

    const editCustomer = () => {
        history.push(`/customers/${customerId}/edit`);
    }

    const onCustomerUpdate = useCallback((data) => {
        setCustomer(data.customer);
    });

    const primaryFooterAction = customer?.default_location
        ? { content: 'Place Order', onAction: () => { placeOrder(customer.default_location.id) }, disabled: customer?.status !== 'approved' }
        : null;

    const secondaryActions = [
        {
            content: 'Merge',
            onAction: () => setShowMergeModal(true),
        }
    ];

    const primaryAction = (customer?.status === 'approved')
        ? {
            content: 'Edit Customer',
            onAction: editCustomer,
        }
        : null;

    const shippingMethod = customer?.shipping_method
        ? `${customer.shipping_method.name} - $${customer.shipping_method.price}`
        : null;

    const getCustomerFinancialInformation = () => {
        const data = [
            { term: 'Total Unpaid', 'description': `$${(customer.orders_sum_total_outstanding ?? 0).toFixed(2)}` },
            { term: 'Credit Enabled', 'description': customer.credit_enabled ? "YES" : "NO" },
            { term: 'Credit Balance', 'description': customer.credit_balance },
        ];

        if (customer.credit_enabled) {
            data.push({ term: 'Credit Expires At', 'description': customer.credit_expires_at });
        }

        return data.concat([
            { term: 'Credit Limit', 'description': customer.additional_data.credit_limit },
            { term: 'Discount', 'description': customer.additional_data.discount },
            { term: 'Purchasing Email', 'description': customer.additional_data.purchasing_email },
            { term: 'Tracking Email', 'description': customer.additional_data.tracking_email },
        ]);
    }

    if (!customer) {
        return (<Frame>
            <LegacyStack distribution={'center'}>
                <Spinner size={'large'} />
            </LegacyStack>
        </Frame>);
    }

    const pageTitle = (customerRow) => {
        if (!customerRow) {
            return '';
        }

        let name = 'Customer ';

        if (customerRow.account_number) {
            name += `#${customerRow.account_number} - `;
        }

        return name + `${customerRow.first_name} ${customerRow.last_name}`;
    };

    return customer && (
        <Frame>
            <Page title={pageTitle(customer)}
                  fullWidth
                  primaryAction={primaryAction}
                  secondaryActions={secondaryActions}
                  backAction={{ content: 'Go back', onAction: () => history.goBack() }}
            >
                <Layout>
                <CustomerOnHold customer={customer}/>
                <MergeModal show={showMergeModal} customer={customer} onClose={closeMergeModal} />
                <Layout.Section>
                    <LegacyCard title="Account Information" sectioned primaryFooterAction={primaryFooterAction}>
                        <DescriptionList items={[
                            { term: 'Company', 'description': customer.company_name },
                            { term: 'Shopify Customer ID', 'description': customer.shopify_customer_id },
                            { term: 'Account Number', 'description': customer.account_number },
                            { term: 'Email', 'description': customer.email },
                            { term: 'Home Phone', 'description': customer.home_phone },
                            { term: 'Cell Phone', 'description': getValue(customer.cell_phone, customer.metafields?.cell_phone)},
                            { term: 'Fax', 'description': getValue(customer.fax, customer.metafields?.fax)},
                            { term: 'Name', 'description': `${customer.additional_data.full_name}` },
                            { term: 'Default Shipping Method', 'description': shippingMethod },
                            { term: 'Airport Code', 'description': customer.airport_code },
                            { term: 'Business Types', 'description': customer.types_of_business?.map(getBusinessTypeName).join(', ') ?? '' },
                            { term: 'State Sales Tax Resale #', 'description': customer.state_sales_tax_resale },
                            { term: 'Business License or Federal Tax ID', 'description': customer.business_license },
                        ]} />
                        <Text variant="headingMd" as="h2">Financial Information</Text>
                        <DescriptionList items={getCustomerFinancialInformation()} />
                    </LegacyCard>

                    <CustomerFiles customer={customer} />

                    <ApprovedOnly customer={customer}>
                        <LocationsCard customer={customer} placeOrder={placeOrder} />
                    </ApprovedOnly>
                </Layout.Section>
                <Layout.Section variant="oneThird">
                    <ChangeStatus customer={customer} setCustomer={setCustomer} backUrl={backUrl} />
                    <ApprovedOnly customer={customer}>
                        <OnHoldToggle customer={customer} setCustomer={setCustomer} />
                        <LegacyCard sectioned title="Notes">
                            <div>{customer.additional_data.notes}</div>
                        </LegacyCard>
                    </ApprovedOnly>
                </Layout.Section>
            </Layout>
            </Page>
        </Frame>
    );
}

export default ShowCustomer;
